<template>
  <div class="home">
    <div class="container mb-5">
      <img
        height="200"
        alt="HiiKER stats"
        class="position-absolute home-stats-bg"
        loading="lazy"
        src="../../images/map-lines.webp"
      />
      <div class="row hero rounded-lg mt-5">
        <div class="col-12 col-md-6">
          <img
            alt="Hiiker hiking app"
            class="img-fluid rounded mb-3"
            loading="lazy"
            src="../../images/upgrade-img.webp"
          />
        </div>
        <div class="col-12 col-md-6">
          <div id="on_boarding_ui_container"></div>
          <div v-if="!currentUser">
            <h1 class="display-1 mb-3">
              <strong>Redeem your purchase</strong>
            </h1>
            <p class="mb-4">Please login/signup to enter your 8-digit redeem code by clicking below.</p>
            <button
              id="loginButton"
              class="btn btn-lg btn-primary mt-3"
              @click="triggerLoginOnboarding"
            >
              CLICK TO LOGIN/SIGNUP
            </button>
            <p class="mt-4"><small>Having trouble? Contact us at <a class="text-yellow" href="mailto:info@hiiker.co">info@hiiker.co</a></small>
            </p>
          </div>

          <div v-if="currentUser">
            <h1 class="display-1 mb-3">
              <strong>Redeem your purchase</strong>
            </h1>
            <p>Enter your 8-digit code below!</p>

            <form id="numberForm" class="mt-4" @submit.prevent="handleRedeemAction">
              <input
                v-for="(digit, index) in digits"
                :key="index"
                type="text"
                :id="'digit' + (index + 1)"
                class="redeem-input"
                maxlength="1"
                required
                v-model="digits[index]"
                @input="handleInput(index, $event)"
                @keydown="handleKeyDown(index, $event)"
                @paste="handlePaste($event, index)" :ref="'digitInput' + index"
                autocomplete="off"
              />
            </form>

            <button
              id="redeemButton"
              type="button"
              ref="redeemButtonRef" class="btn btn-lg btn-primary mt-3"
              @click="handleRedeemAction"
              :disabled="!areAllInputsFilled || isProcessingRedeem"
            >
              {{ isProcessingRedeem ? 'REDEEMING...' : 'REDEEM' }}
            </button>
            <button
              id="clearButton"
              class="btn btn-lg btn-secondary mt-3 ml-1"
              @click="clearAllInputs"
              :disabled="isProcessingRedeem"
            >
              CLEAR
            </button>
            <p class="mt-4"><small>Having trouble redeeming a purchase? Contact us at <a class="text-yellow"
                                                                                         href="mailto:info@hiiker.co">info@hiiker.co</a></small>
            </p>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">

// Adjust these import paths to match your project structure
import graph_client from '../../utils/graphql/graph_client';
import {RedeemGiftCard} from '../../utils/graphql/redeem-graph';
import {OnBoardingGoal} from '../../utils/models/OnBoardingGoal';
import {RevenueCatWrapper} from '../../utils/RevenueCatWrapper';
import emitter from '../../utils/bus'; // Adjust if using a different event system
import { defineComponent } from 'vue'; // Import defineComponent for better TypeScript support

export default defineComponent({ // Use defineComponent
  name: 'RedeemPage',
  data() {
    return {
      digits: Array(8).fill('') as string[], // Typed array
      currentUser: null as any | null, // Typed currentUser
      isProcessingRedeem: false,
      isPasting: false,
    };
  },
  computed: {
    areAllInputsFilled(): boolean { // Typed computed
      return this.digits.every((digit) => digit.trim() !== '' && /^[a-zA-Z0-9]$/.test(digit));
    },
    combinedCode(): string { // Typed computed
      return this.digits.map((d) => d.toUpperCase()).join('');
    },
  },
  methods: {
    // In methods:
    handlePaste(event: ClipboardEvent, startIndex: number) {
      this.isPasting = true;
      event.preventDefault();

      const pastedText = event.clipboardData?.getData('text');
      if (!pastedText) {
        this.$nextTick(() => { this.isPasting = false; });
        return;
      }

      const sanitizedText = pastedText.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      const charsToDistribute = sanitizedText.split('');
      let lastSetIndex = startIndex - 1;

      for (let i = 0; i < charsToDistribute.length; i++) {
        const targetIndex = startIndex + i;
        if (targetIndex < this.digits.length) {
          this.digits[targetIndex] = charsToDistribute[i]; // Vue 3 direct assignment
          lastSetIndex = targetIndex;
        } else {
          break;
        }
      }

      this.$nextTick(() => {
        // ---- CHECK COMPUTED PROPERTY HERE ----
        console.log('Digits after paste:', JSON.stringify(this.digits));
        console.log('areAllInputsFilled in $nextTick:', this.areAllInputsFilled);
        // --------------------------------------

        if (this.areAllInputsFilled) {
          const redeemButton = this.$refs.redeemButtonRef as HTMLButtonElement | undefined;
          if (redeemButton) {
            redeemButton.focus();
            console.log('Focused redeem button');
          }
        } else {
          // ... (rest of your focus logic)
          const nextFocusIndex = lastSetIndex + 1;
          if (nextFocusIndex < this.digits.length) {
            const nextInputRefName = 'digitInput' + nextFocusIndex;
            const nextInputRef = this.$refs[nextInputRefName] as HTMLInputElement | undefined;
            if (nextInputRef) {
              nextInputRef.focus();
              console.log('Focused input:', nextInputRefName);
            }
          } else if (lastSetIndex < this.digits.length && lastSetIndex >= 0) {
            const lastInputRefName = 'digitInput' + lastSetIndex;
            const lastInputRef = this.$refs[lastInputRefName] as HTMLInputElement | undefined;
            if (lastInputRef) {
              lastInputRef.focus();
              console.log('Focused input:', lastInputRefName);
            }
          }
        }
        this.isPasting = false;
      });
    },

    handleInput(index: number, event: Event) { // Typed event and index
      const target = event.target as HTMLInputElement;
      let {value} = target;
      value = value.toUpperCase();

      if (value.length > 1 && !this.isPasting) {
        value = value[0];
      }
      // v-model handles updating this.digits[index] = value;
      // If we need to force a single character for typing when maxlength isn't enough:
      if (!this.isPasting && this.digits[index] !== value ) { // Update only if different and it's typing
        this.digits[index] = value;
      }


      if (this.isPasting) {
        return;
      }

      if (value.length === 1 && /^[a-zA-Z0-9]$/.test(value)) {
        if (index < this.digits.length - 1) {
          this.$nextTick(() => {
            const nextInputRefName = 'digitInput' + (index + 1);
            const nextInputRef = this.$refs[nextInputRefName] as HTMLInputElement | undefined;
            if (nextInputRef) nextInputRef.focus();
          });
        }
      } else if (value.length === 1 && !/^[a-zA-Z0-9]$/.test(value) && !this.isPasting) {
        this.digits[index] = ''; // Clear invalid typed character
      }
    },
    handleKeyDown(index: number, event: KeyboardEvent) { // Typed event and index
      if (this.isPasting) return;

      if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(event.key)) {
        if (event.key === 'Backspace' && index > 0 && this.digits[index]?.length === 0) {
          this.$nextTick(() => {
            const prevInputRefName = 'digitInput' + (index - 1);
            const prevInputRef = this.$refs[prevInputRefName] as HTMLInputElement | undefined;
            if (prevInputRef) prevInputRef.focus();
          });
        }
        return;
      }
      if ((this.digits[index] && this.digits[index].length >= 1 && event.key.length === 1) || (event.key.length === 1 && !/^[a-zA-Z0-9]$/.test(event.key))) {
        event.preventDefault();
      }
    },
    clearAllInputs() {
      this.digits = Array(8).fill('');
      if (this.currentUser) {
        this.$nextTick(() => {
          const firstInputRef = this.$refs.digitInput0 as HTMLInputElement | undefined;
          if (firstInputRef) firstInputRef.focus();
        });
      }
    },
    async redeemGiftCardLogic(giftCardId: string) { // Typed parameter
      this.isProcessingRedeem = true;
      try {
        const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
        if (isProOrProPlus) {
          alert('Account is already PRO+. If this is an error, please contact support@hiiker.co');
          return;
        }
        const response = await graph_client.request(RedeemGiftCard, {
          input: {giftToken: giftCardId.toLowerCase()},
        }) as { redeemGiftCard?: boolean }; // Type the response

        if (response && response.redeemGiftCard) {
          alert('Gift card redeemed successfully! You are now PRO+!');
          if (this.$router) this.$router.push('/profile');
          else window.location.href = '/profile';
        } else {
          alert('Gift card not found, invalid, or already redeemed.');
        }
      } catch (error: any) { // Type error
        console.error("Redeem error:", error);
        let msg = (error.response?.errors?.[0]?.message) || error.message || 'Unknown error during redemption.';
        alert(msg);
      } finally {
        this.isProcessingRedeem = false;
      }
    },
    async handleRedeemAction() {
      if (!this.areAllInputsFilled) {
        alert('Please fill in all 8 digits of your code with valid characters (A-Z, 0-9).');
        return;
      }
      if (this.isProcessingRedeem) return;
      await this.redeemGiftCardLogic(this.combinedCode);
    },
    triggerLoginOnboarding() {
      if (window.onBoardingVue && typeof window.onBoardingVue.launchFor === 'function') {
        window.onBoardingVue.launchFor(OnBoardingGoal.RedeemVoucher);
      } else {
        console.warn('onBoardingVue or launchFor method not found. Cannot trigger login.');
        alert('Login system not available. Please try again later or contact support.');
      }
    },
    populateDigitsFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const codeParam = urlParams.get('code');
      if (codeParam) {
        const sanitizedCode = codeParam.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        const codeArray = sanitizedCode.substring(0, 8).split('');
        this.digits = [...codeArray, ...Array(Math.max(0, 8 - codeArray.length)).fill('')];
      }
    },
    updateUserStatus() { // Parameter `newUser` was removed based on previous feedback
      const oldUser = this.currentUser;
      this.currentUser = (window as any).user || null; // Use `(window as any).user` for TS

      if (this.currentUser && !oldUser) {
        console.log('LOGIC: User just logged in.');
      } else if (!this.currentUser && oldUser) {
        console.log('LOGIC: User just logged out. Resetting UI and form data.');
        this.clearAllInputs();
        this.isProcessingRedeem = false;
      } else if (this.currentUser && oldUser && (!oldUser.id || this.currentUser.id !== oldUser.id)) {
        console.log('LOGIC: User changed (different user). Resetting form data.');
        this.clearAllInputs();
        this.isProcessingRedeem = false;
      }
    },
    setupUserListener() {
      if (emitter && typeof emitter.on === 'function') {
        emitter.on('user:updated', this.updateUserStatus);
      } else console.warn('RedeemPage: Event emitter not found. User status updates will not be received.');
    },
    cleanupUserListener() {
      if (emitter && typeof emitter.off === 'function') {
        emitter.off('user:updated', this.updateUserStatus);
      }
    }
  },
  watch: {
    currentUser(newUser: any | null, oldUser: any | null) { // Typed watcher params
      if (newUser && (!oldUser || (oldUser && newUser.id !== oldUser.id))) {
        this.populateDigitsFromUrl();
        this.$nextTick(() => {
          const firstInputRef = this.$refs.digitInput0 as HTMLInputElement | undefined;
          if (firstInputRef) {
            firstInputRef.focus();
          }
        });
      }
    }
  },
  created() {
    this.currentUser = (window as any).user || null; // Use `(window as any).user` for TS
    this.setupUserListener();
    if (this.currentUser) {
      this.populateDigitsFromUrl();
    }
  },
  mounted() {
    if (this.currentUser) {
      this.$nextTick(() => {
        const firstInputRef = this.$refs.digitInput0 as HTMLInputElement | undefined;
        if (firstInputRef) {
          firstInputRef.focus();
        }
      });
    }
  },
  beforeUnmount() { // Vue 3 lifecycle hook
    this.cleanupUserListener();
  },
});
</script>

<style>
/* Styles remain largely the same, ensure they are appropriate for both states */
#numberForm {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4px;
}

.redeem-input {
  text-align: center;
  font-size: 18px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background: #2d2f35;
  border: 2px solid #434343;
  width: calc(12.5% - 4px);
  max-width: 50px;
  min-width: 35px;
  aspect-ratio: 1/1;
  color: #fff;
  text-transform: uppercase;
  box-sizing: border-box;
  padding: 0;
}

#loginButton:disabled,
#redeemButton:disabled,
#clearButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.redeem-input:focus {
  border-color: #f0ad4e;
  outline: none;
  box-shadow: 0 0 5px rgba(240, 173, 78, 0.5);
}

#on_boarding_ui_container { /* Styles if needed */
}

@media (max-width: 576px) {
  .redeem-input {
    font-size: 16px;
    max-width: 40px;
  }

  #numberForm {
    gap: 2px;
  }

  .display-1 {
    font-size: 2.5rem;
  }
}
</style>
